import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Users, UserCheck } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";

const DashboardTeamActivity = () => {
  // Recent notification activity by user
  const { data: activity } = useQuery({
    queryKey: ["dashboard-team-activity"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("user_name, created_at, vo_number, status, type")
        .order("created_at", { ascending: false })
        .limit(8);
      if (error) throw error;
      return data;
    },
    refetchInterval: 30000,
  });

  // Available employees
  const { data: employees } = useQuery({
    queryKey: ["dashboard-available-employees"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("medewerkers")
        .select("naam");
      if (error) throw error;
      return data;
    },
    refetchInterval: 60000,
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const actionLabel = (status: string) => {
    if (status === "afgerond") return "heeft afgerond";
    if (status === "actie_luijten") return "actie aangemaakt";
    if (status === "actie_decoprof") return "naar Decoprof";
    return "melding bijgewerkt";
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          Team activiteit
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Available count */}
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
          <UserCheck className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium text-green-700 dark:text-green-400">
            {employees?.length ?? "—"} medewerkers beschikbaar
          </span>
        </div>

        {/* Recent activity feed */}
        <div className="space-y-1">
          {activity?.map((a, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors"
            >
              <Avatar className="h-7 w-7 flex-shrink-0">
                <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                  {getInitials(a.user_name || "?")}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">
                  <span className="font-medium">{a.user_name}</span>{" "}
                  <span className="text-muted-foreground">
                    {actionLabel(a.status)}
                  </span>
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {a.vo_number || a.type} ·{" "}
                  {formatDistanceToNow(new Date(a.created_at), {
                    addSuffix: true,
                    locale: nl,
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardTeamActivity;
